/**
 * Filtering for blog listings (the Inspiration pages).
 * ---------------------------------------------------------------------------
 * Shopify has no faceted filtering for blogs, only for collections, so this
 * does the work in the browser. Every post is already on the page, which makes
 * filtering instant and freely combinable: no reload, no extra requests.
 *
 * Each post card carries its values as data attributes (see
 * snippets/blog-filter-values.liquid):
 *
 *     <li data-f-designers="John Pawson|Cecilie Manz" data-f-tones="White">
 *
 * The options in each dropdown are built from those cards, so a filter can only
 * ever offer a value that some post actually has. Within one filter the values
 * are OR'ed ("Green or Blue"), across filters they are AND'ed ("Green AND
 * Bathrooms") — the same behaviour as the product filters.
 *
 * A card can also opt out of one filter entirely with data-f-any-<key>, which
 * means "matches whatever is ticked here". A Journal post that belongs to all
 * of the designers carries data-f-any-designers rather than a list of them.
 *
 * A filter does not have to appear as a dropdown. The bar can also show single
 * values as words of their own — Journal does this, so the reader clicks
 * "Events" rather than opening a "Type" first. Several of those can stand for
 * the same underlying filter, which is why the selection is kept per filter
 * (a bucket) rather than per button: Events and Essays are two buttons but one
 * filter, so having both on shows both kinds of post instead of none.
 *
 * Values are compared case-insensitively and trimmed, so "Fashion designers"
 * and "Fashion Designers" collapse into a single option. The spelling that is
 * shown is the first one encountered.
 *
 * Active filters are mirrored in the query string (?f_tones=Green), so a
 * filtered view can be linked to and survives a reload or a back navigation.
 */

const PARAM_PREFIX = 'f_';

/** Filters that always get a search field, however few values are tagged yet. */
const ALWAYS_SEARCHABLE = ['colour'];

/** Above this many values a filter gets a search field of its own. */
const SEARCH_THRESHOLD = 8;

/** How long the listing stays faded while it is being refiltered. */
const FADE_MS = 140;

/**
 * Dropdowns read down, not across: a list fills one column before a second one
 * starts, so seven filters look alike whether they hold two values or twenty.
 * From this many values a second column opens. Eight is where the sketch put
 * the line, and eight is also what Colour holds today: at exactly eight the
 * list splits four and four, the way the sketch drew it, rather than standing
 * as one long column of eight.
 */
const OPTIONS_PER_COLUMN = 8;

/**
 * How many columns a list may take is no longer a fixed number: it is however
 * many fit across the panel, worked out below from the width of the longest
 * value. Two was too few for Collections and Designers, which hold twenty-odd
 * values and were scrolling when they could have been read at a glance, and any
 * fixed number larger than two would push Colour off the side of the screen.
 *
 * This is the backstop for the case where the panel cannot be measured, which
 * is what happens the first time a list is laid out while its panel is still
 * closed and therefore has no width.
 */
const MAX_OPTION_COLUMNS = 3;

/**
 * The phone layout, asked once and reused. Everything the filter bar does
 * differently on a phone hangs off this: two even columns of values instead of
 * columns as wide as their longest word, because on a 375 pixel screen one long
 * designer name is enough to push the panel wider than the screen and give the
 * whole page a sideways scroll.
 *
 * The number matches the @media query in snippets/blog-filter-bar.liquid. Change
 * one and the other has to follow.
 */
const MOBILE_QUERY = '(max-width: 1023px)';

function isMobileLayout() {
  return window.matchMedia(MOBILE_QUERY).matches;
}

/**
 * Where the reader stood, so that coming back from a case or an article returns
 * them to the picture they tapped rather than to the top of the page. The
 * filters themselves already survive the trip in the address bar; this is the
 * other half of it.
 */
const SCROLL_KEY = 'bleo:blog-filter-scroll';


/** Must match column-gap on .blog-filter-options, in pixels. */
const OPTION_COLUMN_GAP = 28;

/**
 * Colours are not listed alphabetically. They follow the order they are thought
 * of in: light to dark, then the warm end. Everything else — designers,
 * locations, collections — stays alphabetical.
 */
const COLOUR_ORDER = [
  'white',
  'grey',
  'black',
  'beige',
  'brown',
  'green',
  'blue',
  'purple',
  'red',
  'pink',
  'orange',
  'yellow',
];

/**
 * Spellings that mean one of the colours above.
 *
 * Red and pink are two tones, not one. Someone looking for a pink room is not
 * looking for a red one, and the products already say "Red" on its own — the
 * combined "Red/Pink" only ever existed in the text field on the posts, where
 * it sat next to the products' "Red" as a second option for the same thing.
 * A post left with the old spelling still sorts beside red rather than falling
 * to the bottom of the list.
 */
const COLOUR_ALIASES = {
  gray: 'grey',
  'red/pink': 'red',
  'pink/red': 'red',
};

/** The filters whose values follow COLOUR_ORDER. Paint calls its own "Tone". */
const COLOUR_ORDERED_KEYS = ['colour', 'tones'];

/**
 * The other filters that are not alphabetical either, listed in the order they
 * belong in rather than the order the alphabet puts them in: Project runs from
 * the everyday to the specialised, Finish from the flattest to the shiniest and
 * then the ones that are their own thing, Surface from the biggest area in a
 * room down to the smallest and out.
 *
 * Written lowercase, since the comparison is on the normalised value. A value
 * that is not on a list keeps sorting alphabetically and lands under the ones
 * that are, so a new project type turns up at the bottom rather than in the
 * middle. Filters not named here are untouched and stay alphabetical.
 */
const VALUE_ORDER = {
  project_type: [
    'residential',
    'retail',
    'hospitality',
    'public',
    'showroom',
    'workplace',
    'wellness',
  ],
  finishes: [
    'ultra matte',
    'matte',
    'semi gloss',
    'high gloss',
    'minerals',
    'floor',
    'exterior',
  ],
  applications: [
    'walls & ceilings',
    'woodwork trim & metal',
    'kitchens',
    'bathrooms',
    'floors',
    'exterior',
  ],
};

/** Lowercased, whitespace-collapsed key used for comparing two values. */
function normalise(value) {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Where a colour sits in COLOUR_ORDER, or -1 if it is not one we know. Spaces
 * are dropped first, so "Red / Pink" and "Red/Pink" are the same colour.
 */
function colourRank(label) {
  const key = label.replace(/\s+/g, '').toLowerCase();
  const canonical = COLOUR_ALIASES[key] || key;
  return COLOUR_ORDER.indexOf(canonical);
}

/**
 * Where a value sits on its filter's own list, or -1 if it is not on one.
 * Commas are dropped before comparing, so "Woodwork, Trim & Metal" and
 * "Woodwork Trim & Metal" are the same surface however it has been typed.
 */
function orderRank(order, label) {
  const key = normalise(label).replace(/,/g, '').replace(/\s+/g, ' ');
  return order.findIndex((value) => value.replace(/,/g, '') === key);
}

/** The values one card has for one filter, already split and cleaned. */
function cardValues(card, key) {
  const raw = card.getAttribute(`data-f-${key}`);
  if (!raw) return [];
  return raw
    .split('|')
    .map((value) => value.trim().replace(/\s+/g, ' '))
    .filter(Boolean);
}

class BlogFilter {
  constructor(root) {
    this.root = root;
    this.bar = root.querySelector('[data-blog-filter-bar]');
    this.results = root.querySelector('[data-blog-filter-results]');
    if (!this.bar || !this.results) return;

    this.cards = Array.from(this.results.querySelectorAll('[data-blog-filter-item]'));
    this.placeholder = this.results.querySelector('[data-blog-filter-placeholder]');
    this.emptyState = root.querySelector('[data-blog-filter-empty]');
    this.countEl = this.bar.querySelector('[data-blog-filter-result-count]');
    this.clearButton = this.bar.querySelector('[data-blog-filter-clear]');
    this.chipsEl = this.bar.querySelector('[data-blog-filter-chips]');
    // Where the page stood when a panel was opened, or null with none open.
    this.openScrollY = null;
    this.strings = {
      noMatches: this.bar.getAttribute('data-i18n-no-matches') || 'No matches for “[terms]”',
      resultCount: this.bar.getAttribute('data-i18n-result-count') || '[count] of [total]',
    };
    this.perRow = parseInt(this.results.getAttribute('data-per-row'), 10) || 3;
    this.itemColumnWidth = parseInt(this.results.getAttribute('data-item-col-width'), 10) || 4;

    // One bucket per filter, holding what is chosen in it. Buttons point at a
    // bucket rather than owning a set of their own, so the three Type buttons
    // in Journal add up to one Type choice.
    this.buckets = new Map();

    this.groups = Array.from(this.bar.querySelectorAll('[data-blog-filter-group]')).map((el, i) => {
      const key = el.getAttribute('data-blog-filter-group');
      if (!this.buckets.has(key)) this.buckets.set(key, { key, selected: new Set() });

      const chipAttr = el.getAttribute('data-blog-filter-chip');
      return {
        el,
        key,
        // Part of the checkbox ids: two dropdowns could share a filter, and two
        // inputs with the same id would tie their labels to the wrong one.
        index: i,
        bucket: this.buckets.get(key),
        // A word of its own: the values it stands for, already comparable.
        chip: chipAttr ? chipAttr.split('|').map(normalise).filter(Boolean) : null,
        panel: el.querySelector('[data-blog-filter-panel]'),
        toggle: el.querySelector('[data-blog-filter-toggle]'),
        countEl: el.querySelector('[data-blog-filter-count]'),
        optionsEl: el.querySelector('[data-blog-filter-options]'),
        searchWrap: el.querySelector('[data-blog-filter-search-wrap]'),
        searchEl: el.querySelector('[data-blog-filter-search]'),
        noOptionsEl: el.querySelector('[data-blog-filter-no-options]'),
      };
    });

    this.buildOptions();
    this.readUrl();
    this.bindEvents();
    this.updateStickyOffset();
    this.apply();
    this.bindScrollMemory();
    this.restoreScroll();
  }

  /**
   * Remember the scroll position on the way out.
   *
   * pagehide rather than beforeunload: Safari on iOS keeps the page in its back
   * forward cache and never fires beforeunload, which is exactly the journey
   * this is for. The filters are left out of the record on purpose because the
   * address bar already carries them, and two stores of the same thing drift.
   */
  bindScrollMemory() {
    window.addEventListener('pagehide', () => {
      try {
        window.sessionStorage.setItem(
          SCROLL_KEY,
          JSON.stringify({
            url: window.location.pathname + window.location.search,
            y: window.scrollY,
          })
        );
      } catch (error) {
        // Private browsing, or a full quota. Losing the position is a small
        // thing; throwing here would stop the page unloading cleanly.
      }
    });
  }

  /**
   * And put them back, but only when they are returning to the same list with
   * the same filters still on. A remembered position from another page, or from
   * the same page filtered differently, points at nothing.
   *
   * Read once and then dropped, so a fresh visit through the menu starts at the
   * top the way it should. Two frames of grace first: the cards have just been
   * shown or hidden by apply(), and the page is not yet as tall as it will be.
   */
  restoreScroll() {
    let record = null;
    try {
      const raw = window.sessionStorage.getItem(SCROLL_KEY);
      window.sessionStorage.removeItem(SCROLL_KEY);
      if (raw) record = JSON.parse(raw);
    } catch (error) {
      return;
    }

    if (!record || typeof record.y !== 'number' || record.y <= 0) return;
    if (record.url !== window.location.pathname + window.location.search) return;

    const toY = record.y;
    const goto = () => {
      // Never past the end of the document: an image that has not loaded yet
      // leaves the page shorter than it was, and the browser would clamp the
      // scroll to the bottom and stay there.
      const max = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight
      );
      window.scrollTo(0, Math.min(toY, max));
    };

    requestAnimationFrame(() => requestAnimationFrame(goto));
    // Again once the images are in and the page has its real height.
    window.addEventListener('load', goto, { once: true });
  }

  /**
   * Where the bar parks once the page moves.
   *
   * The stylesheet's --sticky-header-height is the height of the header alone,
   * but the header group on these pages holds a second strip under it — the
   * section navigation with the breadcrumb — and it sticks too. A bar parked on
   * the header's height therefore slides in behind that strip and its words
   * disappear the moment the reader scrolls, which is the opposite of what a
   * sticky filter bar is for. Measured off the sticky section itself, so it
   * stays right whether or not that strip is on the page.
   */
  updateStickyOffset() {
    const stuck = Array.from(document.querySelectorAll('.shopify-section-group-header-group'))
      .filter((el) => getComputedStyle(el).position === 'sticky')
      .map((el) => el.offsetHeight);
    if (!stuck.length) return;
    this.bar.style.setProperty('--blog-filter-sticky-top', `${Math.max(...stuck)}px`);
  }

  /** Collect the distinct values each filter has across all cards. */
  buildOptions() {
    this.buckets.forEach((bucket) => {
      const seen = new Map();
      this.cards.forEach((card) => {
        cardValues(card, bucket.key).forEach((value) => {
          const id = normalise(value);
          if (!seen.has(id)) seen.set(id, value);
        });
      });

      const byColour = COLOUR_ORDERED_KEYS.includes(bucket.key);
      const order = VALUE_ORDER[bucket.key];
      bucket.options = Array.from(seen.entries())
        .map(([id, label]) => ({ id, label }))
        .sort((a, b) => {
          let rankA = -1;
          let rankB = -1;
          if (byColour) {
            rankA = colourRank(a.label);
            rankB = colourRank(b.label);
          } else if (order) {
            rankA = orderRank(order, a.label);
            rankB = orderRank(order, b.label);
          }
          // A value we know comes before one we do not, so a new spelling
          // lands at the bottom of the list instead of in the middle of it.
          if (rankA !== rankB && (rankA !== -1 || rankB !== -1)) {
            if (rankA === -1) return 1;
            if (rankB === -1) return -1;
            return rankA - rankB;
          }
          return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
        });
    });

    this.groups.forEach((group) => {
      group.options = group.bucket.options;

      if (group.chip) {
        // Only the values some post actually carries. A category that has not
        // been tagged yet is not offered — and appears by itself the moment the
        // first post gets it, exactly like a value inside a dropdown.
        group.chip = group.chip.filter((value) =>
          group.options.some((option) => option.id === value)
        );
        group.el.hidden = group.chip.length === 0;
        return;
      }

      if (group.options.length === 0) {
        // Nothing tagged yet — hide the whole dropdown rather than show an
        // empty one.
        group.el.hidden = true;
        return;
      }

      // A search field only earns its place once the list is long — except for
      // the colours, which are around 400 and growing, so that one always has
      // it rather than appearing to change behaviour as posts are tagged.
      // display again, not the hidden attribute: the wrapper is a "block".
      group.searchIsShown =
        group.options.length > SEARCH_THRESHOLD || ALWAYS_SEARCHABLE.includes(group.key);
      if (group.searchWrap) {
        group.searchWrap.style.display = group.searchIsShown ? '' : 'none';
      }

      group.optionsEl.innerHTML = '';
      group.options.forEach((option, index) => {
        // Same markup as the product filters: a visually hidden checkbox with
        // the label's dot filled in when it is checked. The look lives in
        // snippets/blog-filter-bar.liquid — Tailwind is purged here, so the
        // states are written as plain CSS on these class names.
        const li = document.createElement('li');
        li.className = 'blog-filter-option';
        li.setAttribute('data-blog-filter-option', option.id);

        const id = `blog-filter-${group.key}-${group.index}-${index}`;

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'filter-checkbox custom-input sr-only';
        input.value = option.id;
        input.id = id;

        const label = document.createElement('label');
        label.htmlFor = id;

        const dot = document.createElement('span');
        dot.className = 'blog-filter-dot';

        label.append(dot, document.createTextNode(option.label));
        li.append(input, label);
        group.optionsEl.append(li);
      });

      this.layoutOptions(group);
    });
  }

  /**
   * Decide how many columns the open list needs. The values are handed to CSS
   * as custom properties; the grid in snippets/blog-filter-bar.liquid fills the
   * first column all the way down before it starts the second, so the list is
   * read the way a list is read.
   *
   * Counted on what is actually showing, not on everything, so a search that
   * narrows twenty values down to three collapses the second column with it.
   */
  layoutOptions(group) {
    const visibleEls = Array.from(
      group.optionsEl.querySelectorAll('[data-blog-filter-option]')
    ).filter((li) => li.style.display !== 'none');
    const visible = visibleEls.length;

    // "+ 1": the split happens *at* eight, not above it, so eight colours read
    // as four and four. The rows are then divided evenly between the columns —
    // eight and one would look like a mistake next to six and six.
    let columns = Math.max(1, Math.ceil((visible + 1) / OPTIONS_PER_COLUMN));

    // Then take away any column that would not fit. Eight rows is the wish, not
    // the rule: Colour holds seventy-five values on Paint, and eight rows there
    // would ask for ten columns and run off the side of the page. Measured from
    // the longest value rather than guessed at, because Designers and Colour
    // hold names of very different lengths and a fixed column width would waste
    // half the panel on one and clip the other.
    // On a phone the measuring below is skipped entirely: the answer is always
    // two columns of equal width, set in the stylesheet, and the only thing left
    // to work out is how many rows that comes to. A single value gets a single
    // column so it does not sit in a half-empty grid.
    if (isMobileLayout()) {
      const mobileColumns = visible > 1 ? 2 : 1;
      group.optionsEl.style.setProperty(
        '--blog-filter-option-columns',
        String(mobileColumns)
      );
      group.optionsEl.style.setProperty(
        '--blog-filter-option-rows',
        String(Math.max(1, Math.ceil(visible / mobileColumns)))
      );
      return;
    }

    const available = group.optionsEl.clientWidth;
    if (available > 0 && visible > 0) {
      let widest = 0;
      visibleEls.forEach((li) => {
        widest = Math.max(widest, li.scrollWidth);
      });
      if (widest > 0) {
        const fits = Math.max(
          1,
          Math.floor((available + OPTION_COLUMN_GAP) / (widest + OPTION_COLUMN_GAP))
        );
        columns = Math.min(columns, fits);
      }
    } else {
      // No width to measure yet, so fall back to the old fixed ceiling and let
      // the next pass, once the panel is open, do it properly.
      columns = Math.min(columns, MAX_OPTION_COLUMNS);
    }

    const rows = Math.max(1, Math.ceil(visible / columns));

    group.optionsEl.style.setProperty('--blog-filter-option-columns', String(columns));
    group.optionsEl.style.setProperty('--blog-filter-option-rows', String(rows));
  }

  bindEvents() {
    this.groups.forEach((group) => {
      if (group.el.hidden) return;

      if (group.chip) {
        group.toggle.addEventListener('click', () => {
          // Off unless every value it stands for is on, so a half-on state
          // (from a link, or from the dropdown of the same filter) completes
          // rather than clears.
          const isOn = group.chip.every((value) => group.bucket.selected.has(value));
          group.chip.forEach((value) => {
            if (isOn) group.bucket.selected.delete(value);
            else group.bucket.selected.add(value);
          });
          this.syncInputs();
          this.requestApply();
          this.writeUrl();
        });
        return;
      }

      group.toggle.addEventListener('click', () => this.toggleGroup(group));

      group.optionsEl.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (input.checked) group.bucket.selected.add(input.value);
        else group.bucket.selected.delete(input.value);
        this.syncInputs();
        this.requestApply();
        this.writeUrl();
        // The panel covers the projects it is filtering, so staying open hides
        // the result of the choice. That is true on a desktop as well as on a
        // phone, so the panel shuts on both. The chips under the bar are what
        // makes this safe: picking a second value is still one click away, and
        // the choices already made are readable with everything shut.
        this.closeAll();
      });

      if (group.searchEl) {
        group.searchEl.addEventListener('input', () => {
          const query = normalise(group.searchEl.value);
          let visible = 0;
          group.optionsEl.querySelectorAll('[data-blog-filter-option]').forEach((li) => {
            const match = !query || li.getAttribute('data-blog-filter-option').includes(query);
            // display, not the hidden attribute: the row carries "flex", which
            // would win over [hidden] { display: none }.
            li.style.display = match ? '' : 'none';
            if (match) visible += 1;
          });
          if (group.noOptionsEl) {
            group.noOptionsEl.style.display = visible > 0 ? 'none' : '';
            group.noOptionsEl.textContent = this.strings.noMatches.replace(
              '[terms]',
              group.searchEl.value
            );
          }
          this.layoutOptions(group);
        });
      }
    });

    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clearAll());
    }

    document.addEventListener('click', (event) => {
      if (!this.bar.contains(event.target)) this.closeAll();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeAll();
    });

    // Scrolling away from an open panel means the reader has moved on to the
    // projects. Measured against where the page was when the panel opened,
    // because opening one can nudge the page by itself under the sticky bar,
    // and a raw scroll listener would read that nudge as the reader leaving.
    window.addEventListener(
      'scroll',
      () => {
        if (this.openScrollY === null) return;
        if (Math.abs(window.scrollY - this.openScrollY) < 24) return;
        this.closeAll();
      },
      { passive: true }
    );

    // Turning a phone on its side, or dragging a desktop window narrower,
    // changes how many columns fit. Only the open list matters: the rest are
    // measured again when they open.
    window.addEventListener('resize', () => {
      this.updateStickyOffset();
      this.groups.forEach((group) => {
        if (group.chip || group.el.hidden) return;
        if (group.panel.classList.contains('hidden')) return;
        this.layoutOptions(group);
      });
    });

    window.addEventListener('popstate', () => {
      this.readUrl();
      this.syncInputs();
      this.apply();
    });
  }

  toggleGroup(group) {
    const isOpen = !group.panel.classList.contains('hidden');
    this.closeAll();
    if (isOpen) return;

    group.panel.classList.remove('hidden');
    group.toggle.setAttribute('aria-expanded', 'true');
    this.setIcon(group, true);
    // Now that the panel is on screen it has a width, so the column count can
    // be worked out for real. Laid out while closed it has none, and every list
    // would fall back to the safe ceiling.
    this.layoutOptions(group);
    // The baseline the scroll listener measures against.
    this.openScrollY = window.scrollY;
    // Not on mobile. Focusing a field there does two unwanted things: iOS zooms
    // the page in when a field under 16px takes focus and never zooms back out,
    // which is the "the page got wider and the right side is cut off" effect,
    // and the keyboard covers the values the reader just asked to see.
    if (group.searchEl && group.searchIsShown && !isMobileLayout()) group.searchEl.focus();
  }

  closeAll() {
    this.openScrollY = null;
    this.groups.forEach((group) => {
      if (group.el.hidden || group.chip) return;
      group.panel.classList.add('hidden');
      group.toggle.setAttribute('aria-expanded', 'false');
      this.setIcon(group, false);
    });
  }

  setIcon(group, open) {
    // Which filter is open, said on the group itself so the stylesheet can mark
    // it. On a phone the chevrons and the plus/minus are hidden and this is what
    // draws the small arrow after the word instead.
    if (open) group.el.setAttribute('data-blog-filter-open', '');
    else group.el.removeAttribute('data-blog-filter-open');

    const chevron = group.toggle.querySelector('[data-blog-filter-chevron]');
    if (chevron) chevron.classList.toggle('rotate-180', open);
    const plus = group.toggle.querySelector('[data-blog-filter-plus]');
    const minus = group.toggle.querySelector('[data-blog-filter-minus]');
    if (plus) plus.classList.toggle('hidden', open);
    if (minus) minus.classList.toggle('hidden', !open);
  }

  /**
   * Same as apply(), but the listing dips out and back in first, so a filter
   * reads as the projects changing rather than as the page jumping. Used for
   * everything the reader does; the first render on page load calls apply()
   * directly, since there is nothing to fade from.
   */
  requestApply() {
    const stillFrames = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (stillFrames) {
      this.apply();
      return;
    }

    this.results.setAttribute('data-blog-filter-busy', '');
    clearTimeout(this.fadeTimer);
    this.fadeTimer = setTimeout(() => {
      this.apply();
      this.results.removeAttribute('data-blog-filter-busy');
    }, FADE_MS);
  }

  /** Show the cards that match every active filter. */
  apply() {
    // Per filter, not per button: two buttons standing for the same filter are
    // one condition to meet, and meeting either of them is enough.
    const active = Array.from(this.buckets.values()).filter((bucket) => bucket.selected.size > 0);
    let matches = 0;

    this.cards.forEach((card) => {
      const isMatch = active.every((bucket) => {
        // A card marked data-f-any-<bucket> belongs to every value in that
        // filter, so it passes no matter what is ticked. It is how a Journal
        // post says "all designers" without listing all of them, and the
        // marker never becomes an option itself: blog-filter-values.liquid
        // keeps it out of data-f-designers.
        if (card.hasAttribute(`data-f-any-${bucket.key}`)) return true;
        const values = cardValues(card, bucket.key).map(normalise);
        return values.some((value) => bucket.selected.has(value));
      });
      card.hidden = !isMatch;
      if (isMatch) matches += 1;
    });

    this.groups.forEach((group) => {
      if (group.el.hidden) return;

      if (group.chip) {
        const isOn = group.chip.every((value) => group.bucket.selected.has(value));
        group.toggle.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        group.el.toggleAttribute('data-blog-filter-active', isOn);
        return;
      }

      // Counted on this dropdown's own values, so a choice made through one of
      // the single words does not show up as a number on a dropdown that never
      // offered it.
      const count = group.options.filter((option) => group.bucket.selected.has(option.id)).length;
      // Non-breaking space: a plain one collapses away against the label.
      group.countEl.textContent = count > 0 ? ` (${count})` : '';
      // Marks the whole dropdown as holding a choice. The stylesheet turns that
      // into a line under the word, which is easier to spot in a row of seven
      // filters than the count on its own.
      group.el.toggleAttribute('data-blog-filter-active', count > 0);
    });

    if (this.countEl) {
      this.countEl.textContent =
        active.length > 0
          ? this.strings.resultCount
              .replace('[count]', String(matches))
              .replace('[total]', String(this.cards.length))
          : '';
    }
    if (this.clearButton) this.clearButton.classList.toggle('hidden', active.length === 0);
    if (this.emptyState) this.emptyState.hidden = matches > 0;

    this.renderChips();
    this.updatePlaceholder(matches);
  }

  /**
   * The chosen values written out under the bar, each one able to remove
   * itself. Built from the buckets rather than from the checkboxes, so a value
   * switched on through one of the single-word filters is listed the same way
   * as one picked out of a dropdown.
   */
  renderChips() {
    if (!this.chipsEl) return;

    const items = [];
    // Two dropdowns can share a bucket, and the same value would then be
    // collected twice. The reader should see one word per choice.
    const listed = new Set();

    this.groups.forEach((group) => {
      if (group.el.hidden) return;

      if (group.chip) {
        const isOn = group.chip.every((value) => group.bucket.selected.has(value));
        const id = `${group.key}:${group.chip.join('|')}`;
        if (!isOn || listed.has(id)) return;
        listed.add(id);
        items.push({
          label: group.toggle.textContent.trim(),
          bucket: group.bucket,
          values: group.chip,
        });
        return;
      }

      group.options.forEach((option) => {
        const id = `${group.key}:${option.id}`;
        if (!group.bucket.selected.has(option.id) || listed.has(id)) return;
        listed.add(id);
        items.push({ label: option.label, bucket: group.bucket, values: [option.id] });
      });
    });

    this.chipsEl.textContent = '';
    this.chipsEl.hidden = items.length === 0;
    if (items.length === 0) return;

    items.forEach((item) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'blog-filter-chip';
      // Read out as an action rather than as a stray word, since the cross
      // itself is decoration to a screen reader.
      button.setAttribute('aria-label', `Remove ${item.label}`);

      const text = document.createElement('span');
      text.textContent = item.label;

      const cross = document.createElement('span');
      cross.className = 'blog-filter-chip__x';
      cross.setAttribute('aria-hidden', 'true');
      cross.textContent = '✕';

      button.append(text, cross);
      button.addEventListener('click', () => {
        item.values.forEach((value) => item.bucket.selected.delete(value));
        this.syncInputs();
        this.requestApply();
        this.writeUrl();
      });

      li.append(button);
      this.chipsEl.append(li);
    });
  }

  /**
   * The grid ends with a spacer that fills the last row. It is sized on the
   * server for the unfiltered count, so it has to be recalculated whenever the
   * number of visible cards changes.
   */
  updatePlaceholder(matches) {
    if (!this.placeholder) return;
    const onLastRow = matches % this.perRow;
    if (matches === 0 || onLastRow === 0) {
      // The spacer is lg:block, so it has to be hidden through display.
      this.placeholder.style.display = 'none';
      return;
    }
    this.placeholder.style.display = '';
    this.placeholder.style.setProperty(
      '--placeholder-width',
      String((this.perRow - onLastRow) * this.itemColumnWidth)
    );
  }

  clearAll() {
    this.buckets.forEach((bucket) => bucket.selected.clear());
    this.syncInputs();
    this.requestApply();
    this.writeUrl();
  }

  /** Push the chosen values back onto the inputs (URL load, clear all). */
  syncInputs() {
    this.groups.forEach((group) => {
      if (group.el.hidden || group.chip) return;
      group.optionsEl.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.checked = group.bucket.selected.has(input.value);
      });
    });
  }

  readUrl() {
    const params = new URLSearchParams(window.location.search);
    this.buckets.forEach((bucket) => {
      bucket.selected.clear();
      // One parameter per value rather than one comma separated parameter:
      // a value can itself contain a comma ("Paris, France"), which would
      // otherwise be read back as two filters that match nothing.
      params
        .getAll(PARAM_PREFIX + bucket.key)
        .map((value) => normalise(value))
        .filter(Boolean)
        // Ignore values that no longer exist, so an old link never filters
        // everything away.
        .filter((value) => bucket.options.some((option) => option.id === value))
        .forEach((value) => bucket.selected.add(value));
    });
    this.syncInputs();
  }

  writeUrl() {
    const params = new URLSearchParams(window.location.search);
    this.buckets.forEach((bucket) => {
      const name = PARAM_PREFIX + bucket.key;
      // Deleted either way: what is left is written back from scratch, so a
      // value that has just been switched off cannot linger in the address.
      params.delete(name);
      if (bucket.selected.size === 0) return;
      // Store the readable spelling, not the lowercased comparison key.
      bucket.options
        .filter((option) => bucket.selected.has(option.id))
        .forEach((option) => params.append(name, option.label));
    });

    const query = params.toString();
    const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    window.history.replaceState({}, '', url);
  }
}

document.querySelectorAll('[data-blog-filter-root]').forEach((root) => new BlogFilter(root));
